import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, LIST_WIZARD, LIST_WIZARD_ANIM, MOBILE, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';

/* The list wizard's steps, in a row beside the sheet's title: each a mark (a check once done, else its number)
   and its name, a short line between one and the next. Colours and timings are tokens, spacing is the SPACE
   scale. */

export const Steps = styled.ol<{ $pills?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;

  /* Pills: on a phone the steps take a row of their own under the title, across its whole width. */
  ${({ $pills }) =>
    $pills &&
    css`
      flex-wrap: wrap;

      ${MOBILE} {
        flex: 1 1 100%;
        flex-wrap: nowrap;
        gap: ${SPACE[4]};
      }
    `}
`;

/* A short connector line in front of every step but the first; with pills, a chevron (Sep) instead. The current step's
   item takes the row's room on a phone ($grow). */
export const Step = styled.li<{ $pills?: boolean; $grow?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;

  ${({ $pills }) =>
    !$pills &&
    css`
      & + &::before {
        content: '';
        flex: none;
        width: 20px;
        height: 1px;
        background: ${COLORS.hairline};
      }
    `}

  ${({ $pills, $grow }) =>
    $pills &&
    css`
      ${MOBILE} {
        gap: ${SPACE[4]};
        ${$grow ? 'flex: 1 1 auto;' : 'flex: none;'}
      }
    `}
`;

/* The chevron between two pills, pointing on to the next step. */
export const Sep = styled.span`
  flex: none;
  display: inline-flex;
  color: ${COLORS.textSubdued};
  transform: rotate(-90deg);
`;

/* On a phone the pills are narrower at the sides, which leaves the one name that shows the room for "Name and layout"
   at 360px. */
const stepBox = css`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  padding: ${SPACE[4]} ${SPACE[8]};
  border-radius: 999px;

  ${MOBILE} {
    padding: ${SPACE[4]};
  }
`;

export type NumState = 'done' | 'current' | 'ahead';

const pillMs = `${LIST_WIZARD_ANIM.PILL_MS}ms ${LIST_WIZARD_ANIM.EASE}`;

/* The pill look (the prototype's): 40px tall, outlined, the current one filled in the brand orange and a step behind
   outlined in it. Its colours, outline and padding ease between states. On a phone the current pill stretches across
   the row and the others are 40px rounds with their mark alone. 14px and 16px insets are the prototype's, as are the
   15px of the current step's words. */
const pillLook: Record<NumState, ReturnType<typeof css>> = {
  current: css`
    background: ${COLORS.ctaSolid};
    border-color: ${COLORS.ctaSolid};
    color: ${COLORS.ctaSolidText};
    font-size: 15px;
    font-weight: 700;
    padding-right: 16px;
  `,
  done: css`
    border-color: ${LIST_WIZARD.doneLine};
    color: ${COLORS.text};
  `,
  ahead: css``,
};

const pillBox = (state: NumState): ReturnType<typeof css> => css`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  height: 40px;
  box-sizing: border-box;
  padding: 0 14px 0 ${SPACE[8]};
  border-radius: 99px;
  border: 1px solid ${LIST_WIZARD.line};
  background: transparent;
  color: ${LIST_WIZARD.muted};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  transition:
    background ${pillMs},
    color ${pillMs},
    border-color ${pillMs},
    padding ${pillMs};
  ${pillLook[state]}

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    ${state === 'current'
      ? css`
          flex: 1 1 auto;
          justify-content: flex-start;
          font-size: 14px;
          padding-right: ${SPACE[12]};
        `
      : css`
          width: 40px;
          padding: 0;
          justify-content: center;
        `}
  }
`;

/* A step that can be gone to: every step while editing, the steps behind while adding. */
export const StepBtn = styled.button<{ $pills?: boolean; $state: NumState }>`
  ${({ $pills, $state }) =>
    $pills
      ? css`
          ${pillBox($state)}
          cursor: pointer;

          &:hover {
            background: ${$state === 'current' ? COLORS.ctaSolid : COLORS.ctaTint};
          }
        `
      : css`
          ${stepBox}
          border: 0;
          background: transparent;
          font-family: inherit;
          cursor: pointer;
          transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

          &:hover {
            background: ${COLORS.hoverWash};
          }
        `}

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }
`;

/* A step that cannot be gone to from here: the current one and those ahead while adding. */
export const StepText = styled.span<{ $pills?: boolean; $state: NumState }>`
  ${({ $pills, $state }) => ($pills ? pillBox($state) : stepBox)}
`;

const numLook: Record<NumState, ReturnType<typeof css>> = {
  done: css`
    background: ${COLORS.ctaTint};
    border-color: ${COLORS.ctaBorder};
    color: ${COLORS.ctaSolid};
  `,
  current: css`
    background: ${COLORS.ctaSolid};
    border-color: ${COLORS.ctaSolid};
    color: ${COLORS.ctaSolidText};
  `,
  ahead: css`
    background: transparent;
    border-color: ${COLORS.hairline};
    color: ${COLORS.textMuted};
  `,
};

/* The step's mark: filled for the current step, outlined for one ahead, a check for one behind. */
export const Num = styled.span<{ $state: NumState }>`
  flex: none;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  ${({ $state }) => numLook[$state]}
`;

/* Out of sight but still read: a step button stays named by its words where they do not show. */
const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
`;

/* The step's name, or a summary once it is behind. On a phone only the current step's shows; the others are
   hidden from sight only, so their buttons keep their names.
   Where the header leaves it less room than its words want, it is clipped with an ellipsis rather than run on under
   the sheet's close button. Only its drawing is clipped, so the step button is still named by all of it. On a phone
   the one that shows also takes its pill's right padding, which ends inside the header, so it is never drawn under
   the close button. */
export const Label = styled(Text).attrs({ tag: 'span' as const })<{ $current: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: ${({ $current }) => ($current ? 700 : 600)};
  color: ${({ $current }) => ($current ? COLORS.heading : COLORS.textMuted)};
  white-space: nowrap;

  ${MOBILE} {
    ${({ $current }) => ($current ? `margin-right: -${SPACE[4]};` : visuallyHidden)}
  }
`;

/* ----------------------------------------------------------- the pills' parts */

const pillNumLook: Record<NumState, ReturnType<typeof css>> = {
  current: css`
    background: ${COLORS.ctaSolidText};
    border-color: ${COLORS.ctaSolidText};
    color: ${LIST_WIZARD.soft};
  `,
  done: css`
    background: ${LIST_WIZARD.deep};
    border-color: ${LIST_WIZARD.deep};
    color: ${LIST_WIZARD.soft};
  `,
  ahead: css`
    border-color: ${COLORS.textSubdued};
  `,
};

/* A pill's mark: its number in a 24px round, a check once the step is behind. */
export const PillNum = styled.span<{ $state: NumState }>`
  flex: none;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border-radius: 99px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition:
    background ${pillMs},
    color ${pillMs},
    border-color ${pillMs};
  ${({ $state }) => pillNumLook[$state]}

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

const pop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/* The check of a step behind, popping in as the step falls behind. */
export const PillCheck = styled.span`
  display: inline-flex;
  animation: ${pop} ${pillMs} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* A pill's words. On a phone only the current pill's show; the others are hidden from sight only, so their buttons keep
   their names. Where the row is too narrow for them, clipped with an ellipsis. */
export const PillLabel = styled(Text).attrs({ tag: 'span' as const })<{ $current: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    ${({ $current }) => ($current ? '' : visuallyHidden)}
  }
`;

/* "2 of 3" at the end of the current pill, on a phone only. */
export const PillOf = styled.span`
  display: none;

  ${MOBILE} {
    display: inline;
    flex: none;
    margin-left: auto;
    font-size: 12px;
    font-weight: 700;
    opacity: 0.75;
    white-space: nowrap;
  }
`;
